import { api, apiUrl } from "../../shared/services/service";
import type {
  CreateVesselDryDockInput,
  CreateVesselHullEventInput,
  PaintOption,
  UpdateVesselDryDockInput,
  UpdateVesselHullEventInput,
  VesselDryDock,
  VesselHullEvent,
} from "../models/uw-ops.models";
import axios from "axios";

type DataResponse<T> = { data: T };
const vesselPath = (imoNumber: string) => `${apiUrl}/hull-cleaning/vessels/${encodeURIComponent(imoNumber)}`;

export const getUwOpsErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.error ?? error.response?.data?.message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return error instanceof Error && error.message.trim() ? error.message : fallback;
};

export async function getVesselDryDocks(imoNumber: string): Promise<VesselDryDock[]> {
  const response = await api.get<DataResponse<VesselDryDock[]>>(`${vesselPath(imoNumber)}/dry-docks`);
  return response.data.data ?? [];
}

export async function createVesselDryDock(imoNumber: string, input: CreateVesselDryDockInput): Promise<VesselDryDock> {
  const response = await api.post<DataResponse<VesselDryDock>>(`${vesselPath(imoNumber)}/dry-docks`, input);
  return response.data.data;
}

export async function updateVesselDryDock(
  imoNumber: string,
  dryDockDate: string,
  input: UpdateVesselDryDockInput,
): Promise<VesselDryDock> {
  const response = await api.post<DataResponse<VesselDryDock>>(
    `${vesselPath(imoNumber)}/dry-docks/${encodeURIComponent(dryDockDate)}/update`,
    input,
  );
  return response.data.data;
}

export async function deleteVesselDryDock(imoNumber: string, dryDockDate: string): Promise<void> {
  await api.post(`${vesselPath(imoNumber)}/dry-docks/${encodeURIComponent(dryDockDate)}/delete`);
}

export async function getVesselHullEvents(imoNumber: string): Promise<VesselHullEvent[]> {
  const response = await api.get<DataResponse<VesselHullEvent[]>>(`${vesselPath(imoNumber)}/events`);
  return response.data.data ?? [];
}

export async function createVesselHullEvent(
  imoNumber: string,
  input: CreateVesselHullEventInput,
): Promise<VesselHullEvent> {
  const response = await api.post<DataResponse<VesselHullEvent>>(`${vesselPath(imoNumber)}/events`, input);
  return response.data.data;
}

export async function updateVesselHullEvent(
  imoNumber: string,
  eventId: string,
  input: UpdateVesselHullEventInput,
): Promise<VesselHullEvent> {
  const response = await api.post<DataResponse<VesselHullEvent>>(
    `${vesselPath(imoNumber)}/events/${encodeURIComponent(eventId)}/update`,
    input,
  );
  return response.data.data;
}

export async function deleteVesselHullEvent(imoNumber: string, eventId: string): Promise<void> {
  await api.post(`${vesselPath(imoNumber)}/events/${encodeURIComponent(eventId)}/delete`);
}

export async function getHullCleaningPaintOptions(): Promise<PaintOption[]> {
  const response = await api.get<DataResponse<PaintOption[]>>(`${apiUrl}/hull-cleaning/paint-options`);
  return response.data.data ?? [];
}
